/**
 * Thin fetch client for the JSON API. Same-origin credentials carry the session
 * cookie. Every call returns a discriminated result so callers handle errors
 * without try/catch at the call site.
 *
 * The request AND response shapes live in src/shared/contract as zod schemas
 * (HS2-HAQYG6, HS2-VTMV71); this module imports the inferred `z.infer` TYPES
 * only (`import type`), so no zod runtime reaches the SPA bundle. The response
 * types are re-exported below so the ~40 consumer views keep importing them
 * from './data/api.js' unchanged.
 */
import type {
  AnalysisListResponse,
  AnalysisReport,
  DemandAssumptions,
  ManualBenchmark,
} from '../../shared/contract/analysis.js';
import type {
  ChatCardAction,
  ConversationDetailResponse,
  ConversationListResponse,
  CreateConversation,
  SendChatMessage,
  UpdateConversation,
} from '../../shared/contract/conversations.js';
import type {
  BulkApplyRecommendations as BulkApplyRecommendationsBody,
  DecisionsListResponse,
  DecisionSummaryT,
  RecordDecision,
} from '../../shared/contract/decisions.js';
import type {
  AssignableMembersResponse,
  AssignDemand,
  CreateDemand,
  CreateDocumentAnnotation,
  DemandChangeResponse,
  DemandListResponse,
  DocumentAnnotation,
  DocumentAnnotationsResponse,
  LineItem,
  MoveDocument,
  MoveDocumentAnnotation,
  RequesterWorkspaceResponse,
  ReturnDemand,
  UpdateDemand,
  UpdateLineItem,
  WorkspaceResponse,
} from '../../shared/contract/demands.js';
import type { FormDraft, FormDraftResponse, SavedFormDraftResponse } from '../../shared/contract/forms.js';
import type {
  Archive,
  BackgroundListResponse,
  DocumentRevisionListResponse,
  FactListResponse,
  ManualFact,
  Project,
  ProjectListResponse,
  SourceEvidenceResponse,
  SourceRowSearchResponse,
  UpdateFact,
  UpdateProject,
} from '../../shared/contract/library.js';
import type {
  AiBudget,
  AiUsageSummary,
  Assumptions,
  CreateOrg,
  Me,
  MemberInvitationResponse,
  MemberInvite,
  MemberRolesPatch,
  MyOrganizationsResponse,
  OrgSettingsResponse,
  PreferencesPatch,
  Profile,
  Site,
  Thresholds,
} from '../../shared/contract/orgs.js';
import type {
  DemandSavingsT,
  FailOrder,
  OrgSavingsT,
  RecordOrder,
  RecordOutcome,
  RecordReceipt,
} from '../../shared/contract/outcomes.js';
import type {
  ManualQuotation,
  MergeVendors,
  QuoteMatrix,
  Remap,
  UpdateQuotation,
  VendorDetailT,
  VendorInput,
  VendorsOverviewT,
} from '../../shared/contract/quotations.js';
import type {
  BulkDecisionAck,
  ChatApplyAck,
  ChatMessageAck,
  DemandIdResponse,
  IdFileResponse,
  IdKindResponse,
  IdLineResponse,
  IdNameResponse,
  IdResponse,
  IdStatusResponse,
  OkJobResponse,
  OkLineItemResponse,
  OkResponse,
  OkStatusResponse,
  OutcomeAck,
  PreferencesResponse,
  PrivacyJobAck,
  PrivacyStatus,
  SignedUrlResponse,
} from '../../shared/contract/responses.js';

// Re-export the response types so existing consumers keep importing them from
// './data/api.js'. (`export type`, so still no zod value lands in the bundle.)
export type {
  AnalysisReport,
  AnalysisRowT,
  AnalysisSummaryT,
  BenchmarkRowT,
  EstimateRowT,
  VerdictRowT,
} from '../../shared/contract/analysis.js';
export type {
  ChatActivityT,
  ChatCardT,
  ChatCitationT,
  ChatMessageT,
  ConversationSummaryT,
  ConversationT,
} from '../../shared/contract/conversations.js';
export type { DecisionSummaryT, DecisionT } from '../../shared/contract/decisions.js';
export type {
  AssignableMembersResponse,
  DemandChangeResponse,
  DemandListItem,
  DemandListResponse,
} from '../../shared/contract/demands.js';
export type { BackgroundDocT, FactT, ProjectT } from '../../shared/contract/library.js';
export type { AiUsageSummary, Me } from '../../shared/contract/orgs.js';
export type { DemandSavingsT, LineOrderT, LineSavingT, OrgSavingsT } from '../../shared/contract/outcomes.js';
export type {
  MatrixCell,
  MatrixVendorColumn,
  QuoteMatrix,
  UnmatchedQuoteLine,
  VendorDetailT,
  VendorsOverviewT,
} from '../../shared/contract/quotations.js';

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T | null;
  error?: string;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<ApiResult<T>> {
  const res = await fetch(path, {
    method,
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  const error =
    !res.ok && data !== null && typeof data === 'object' && 'error' in data ? String(data.error) : undefined;
  return { ok: res.ok, status: res.status, data: res.ok ? (data as T) : null, error };
}

export const api = {
  me: () => request<Me>('GET', '/api/me'),
  updatePreferences: (body: PreferencesPatch) => request<PreferencesResponse>('PATCH', '/api/me/preferences', body),
  myOrgs: () => request<MyOrganizationsResponse>('GET', '/api/my/orgs'),
  switchOrg: (orgId: string) => request<OkResponse>('POST', `/api/my/orgs/${orgId}/switch`),
  createOrg: (body: CreateOrg) => request<IdNameResponse>('POST', '/api/orgs', body),
  getSettings: (orgId: string) => request<OrgSettingsResponse>('GET', `/api/orgs/${orgId}/settings`),
  inviteMember: (orgId: string, body: MemberInvite) =>
    request<MemberInvitationResponse>('POST', `/api/orgs/${orgId}/members`, body),
  resendInvitation: (orgId: string, memberId: string) =>
    request<MemberInvitationResponse>('POST', `/api/orgs/${orgId}/members/${memberId}/invitation`),
  updateMemberRoles: (orgId: string, memberId: string, body: MemberRolesPatch) =>
    request<OkResponse>('PUT', `/api/orgs/${orgId}/members/${memberId}/roles`, body),
  removeMember: (orgId: string, memberId: string) =>
    request<OkResponse>('DELETE', `/api/orgs/${orgId}/members/${memberId}`),
  createSite: (orgId: string, body: Site) => request<IdNameResponse>('POST', `/api/orgs/${orgId}/sites`, body),
  updateSite: (orgId: string, siteId: string, body: Partial<Site>) =>
    request<OkResponse>('PUT', `/api/orgs/${orgId}/sites/${siteId}`, body),
  updateProfile: (orgId: string, body: Profile) => request<OkResponse>('PUT', `/api/orgs/${orgId}/profile`, body),
  updateThresholds: (orgId: string, body: Thresholds) =>
    request<OkResponse>('PUT', `/api/orgs/${orgId}/thresholds`, body),
  updateAssumptions: (orgId: string, body: Assumptions) =>
    request<OkResponse>('PUT', `/api/orgs/${orgId}/assumptions`, body),
  getAiUsage: (orgId: string) => request<AiUsageSummary>('GET', `/api/orgs/${orgId}/ai-usage`),
  updateAiBudget: (orgId: string, body: AiBudget) => request<OkResponse>('PUT', `/api/orgs/${orgId}/ai-budget`, body),
  signout: () => request<OkResponse>('POST', '/auth/signout'),
  stopActing: () => request<OkResponse>('POST', '/auth/stop-acting'),
  exportOrg: (orgId: string) => request<PrivacyJobAck>('POST', `/api/orgs/${orgId}/export`),
  pollExport: (orgId: string, jobId: string) => request<PrivacyStatus>('GET', `/api/orgs/${orgId}/export/${jobId}`),
  deleteOrg: (orgId: string) => request<PrivacyJobAck>('DELETE', `/api/orgs/${orgId}`),

  listDemands: (orgId: string, query = '') => request<DemandListResponse>('GET', `/api/orgs/${orgId}/demands${query}`),
  async pollDemandChanges(
    orgId: string,
    since?: number,
    signal?: AbortSignal,
    timeoutMs = 25_000,
  ): Promise<DemandChangeResponse> {
    const query = new URLSearchParams({ timeout_ms: String(timeoutMs) });
    if (since !== undefined) query.set('since', String(since));
    const res = await fetch(`/api/orgs/${encodeURIComponent(orgId)}/demand-changes?${query.toString()}`, {
      credentials: 'same-origin',
      signal,
    });
    if (!res.ok) throw new Error(`demand change poll failed (${String(res.status)})`);
    return res.json() as Promise<DemandChangeResponse>;
  },
  getDemand: (orgId: string, id: string) =>
    request<WorkspaceResponse | RequesterWorkspaceResponse>('GET', `/api/orgs/${orgId}/demands/${id}`),
  createDemand: (orgId: string, body: CreateDemand) =>
    request<IdStatusResponse>('POST', `/api/orgs/${orgId}/demands`, body),
  demoDemand: () => request<DemandIdResponse>('POST', '/api/dev/demo-demand'),
  getFormDraft: (orgId: string, formId: string) =>
    request<FormDraftResponse>('GET', `/api/orgs/${orgId}/forms/${formId}/draft`),
  saveFormDraft: (orgId: string, formId: string, answers: FormDraft['answers']) =>
    request<SavedFormDraftResponse>('PUT', `/api/orgs/${orgId}/forms/${formId}/draft`, {
      answers,
    } satisfies FormDraft),
  clearFormDraft: (orgId: string, formId: string) =>
    request<OkResponse>('DELETE', `/api/orgs/${orgId}/forms/${formId}/draft`),
  getDocumentUrl: (orgId: string, documentId: string) =>
    request<SignedUrlResponse>('GET', `/api/orgs/${orgId}/documents/${documentId}/url`),
  getDocumentAnnotations: (orgId: string, demandId: string, documentId: string) =>
    request<DocumentAnnotationsResponse>(
      'GET',
      `/api/orgs/${orgId}/demands/${demandId}/documents/${documentId}/annotations`,
    ),
  addDocumentAnnotation: (orgId: string, demandId: string, documentId: string, body: CreateDocumentAnnotation) =>
    request<DocumentAnnotation>(
      'POST',
      `/api/orgs/${orgId}/demands/${demandId}/documents/${documentId}/annotations`,
      body,
    ),
  moveDocumentAnnotation: (
    orgId: string,
    demandId: string,
    documentId: string,
    annotationId: string,
    point: MoveDocumentAnnotation,
  ) =>
    request<DocumentAnnotation>(
      'PATCH',
      `/api/orgs/${orgId}/demands/${demandId}/documents/${documentId}/annotations/${annotationId}`,
      point,
    ),
  deleteDocumentAnnotation: (orgId: string, demandId: string, documentId: string, annotationId: string) =>
    request<OkResponse>(
      'DELETE',
      `/api/orgs/${orgId}/demands/${demandId}/documents/${documentId}/annotations/${annotationId}`,
    ),
  deleteDocument: (orgId: string, demandId: string, documentId: string) =>
    request<OkResponse>('DELETE', `/api/orgs/${orgId}/demands/${demandId}/documents/${documentId}`),
  async replaceDocument(
    orgId: string,
    demandId: string,
    documentId: string,
    file: File,
  ): Promise<ApiResult<IdFileResponse>> {
    const form = new FormData();
    form.set('file', file);
    const res = await fetch(`/api/orgs/${orgId}/demands/${demandId}/documents/${documentId}`, {
      method: 'PUT',
      body: form,
      credentials: 'same-origin',
    });
    let data: unknown;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    const error =
      !res.ok && data !== null && typeof data === 'object' && 'error' in data ? String(data.error) : undefined;
    return {
      ok: res.ok,
      status: res.status,
      data: res.ok ? (data as IdFileResponse) : null,
      error,
    };
  },

  // The demand's responsible staff member (FR-5.20, HS2-C63T9N).
  listAssignees: (orgId: string) => request<AssignableMembersResponse>('GET', `/api/orgs/${orgId}/assignees`),
  assignDemand: (orgId: string, demandId: string, body: AssignDemand) =>
    request<OkResponse>('PUT', `/api/orgs/${orgId}/demands/${demandId}/assignee`, body),
  // Intake / line-item review (Flow F2, HS2-6C1WJ0).
  updateDemand: (orgId: string, demandId: string, body: UpdateDemand) =>
    request<OkStatusResponse>('PATCH', `/api/orgs/${orgId}/demands/${demandId}`, body),
  addLineItem: (orgId: string, demandId: string, body: LineItem) =>
    request<IdLineResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/line-items`, body),
  updateLineItem: (orgId: string, demandId: string, lineId: string, body: UpdateLineItem) =>
    request<OkResponse>('PATCH', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}`, body),
  deleteLineItem: (orgId: string, demandId: string, lineId: string) =>
    request<OkResponse>('DELETE', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}`),
  confirmDemand: (orgId: string, demandId: string) =>
    request<OkStatusResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/confirm`),
  returnDemand: (orgId: string, demandId: string, body: ReturnDemand) =>
    request<OkStatusResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/return`, body),
  resubmitDemand: (orgId: string, demandId: string) =>
    request<OkStatusResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/resubmit`),
  async uploadDocument(orgId: string, demandId: string, file: File, kind: string): Promise<ApiResult<IdFileResponse>> {
    const form = new FormData();
    form.set('file', file);
    form.set('kind', kind);
    const res = await fetch(`/api/orgs/${orgId}/demands/${demandId}/documents`, {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
    });
    let data: unknown;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    const error =
      !res.ok && data !== null && typeof data === 'object' && 'error' in data ? String(data.error) : undefined;
    return {
      ok: res.ok,
      status: res.status,
      data: res.ok ? (data as IdFileResponse) : null,
      error,
    };
  },

  // Quotation intake / price matrix (Flow F3, HS2-24PX6K).
  getQuoteMatrix: (orgId: string, demandId: string) =>
    request<QuoteMatrix>('GET', `/api/orgs/${orgId}/demands/${demandId}/quotes`),
  // Reclassify a supporting document as a vendor quotation (HS2-FKJB32).
  reclassifyToQuotation: (orgId: string, demandId: string, docId: string) =>
    request<IdKindResponse>(
      'POST',
      `/api/orgs/${orgId}/demands/${demandId}/documents/${docId}/reclassify-as-quotation`,
    ),
  retryQuotationExtraction: (orgId: string, demandId: string, docId: string) =>
    request<IdKindResponse>(
      'POST',
      `/api/orgs/${orgId}/demands/${demandId}/documents/${docId}/retry-quotation-extraction`,
    ),
  moveDocument: (orgId: string, demandId: string, docId: string, kind: MoveDocument['kind']) =>
    request<IdKindResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/documents/${docId}/move`, {
      kind,
    } satisfies MoveDocument),
  promoteDocument: (orgId: string, docId: string, projectId?: string) =>
    request<IdKindResponse>('POST', `/api/orgs/${orgId}/documents/${docId}/promote`, { projectId }),
  addQuotation: (orgId: string, demandId: string, body: ManualQuotation) =>
    request<IdResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/quotes`, body),
  updateQuotation: (orgId: string, demandId: string, quotationId: string, body: UpdateQuotation) =>
    request<OkResponse>('PATCH', `/api/orgs/${orgId}/demands/${demandId}/quotes/${quotationId}`, body),
  remapQuoteLine: (orgId: string, demandId: string, quoteLineId: string, lineItemId: string | null) =>
    request<OkResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/quote-lines/${quoteLineId}/remap`, {
      lineItemId,
    } satisfies Remap),
  ignoreQuoteLine: (orgId: string, demandId: string, quoteLineId: string) =>
    request<OkResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/quote-lines/${quoteLineId}/ignore`),
  addQuoteLineAsLineItem: (orgId: string, demandId: string, quoteLineId: string) =>
    request<OkLineItemResponse>(
      'POST',
      `/api/orgs/${orgId}/demands/${demandId}/quote-lines/${quoteLineId}/add-as-line`,
    ),
  mergeVendors: (orgId: string, fromVendorId: string, intoVendorId: string) =>
    request<OkResponse>('POST', `/api/orgs/${orgId}/vendors/merge`, {
      fromVendorId,
      intoVendorId,
    } satisfies MergeVendors),
  vendorsOverview: (orgId: string) => request<VendorsOverviewT>('GET', `/api/orgs/${orgId}/vendors/overview`),
  vendorHistory: (orgId: string, vendorId: string) =>
    request<VendorDetailT>('GET', `/api/orgs/${orgId}/vendors/${vendorId}/history`),
  createVendor: (orgId: string, body: VendorInput) =>
    request<IdNameResponse>('POST', `/api/orgs/${orgId}/vendors`, body),
  updateVendor: (orgId: string, vendorId: string, body: VendorInput) =>
    request<IdNameResponse>('PUT', `/api/orgs/${orgId}/vendors/${vendorId}`, body),

  // Background library (Flow F9, HS2-G8R98W).
  listProjects: (orgId: string) => request<ProjectListResponse>('GET', `/api/orgs/${orgId}/projects`),
  createProject: (orgId: string, body: Project) => request<IdResponse>('POST', `/api/orgs/${orgId}/projects`, body),
  updateProject: (orgId: string, projectId: string, body: UpdateProject) =>
    request<OkResponse>('PATCH', `/api/orgs/${orgId}/projects/${projectId}`, body),
  listBackground: (orgId: string, projectId?: string) =>
    request<BackgroundListResponse>(
      'GET',
      `/api/orgs/${orgId}/background${projectId !== undefined ? `?project=${projectId}` : ''}`,
    ),
  listFacts: (orgId: string, projectId?: string) =>
    request<FactListResponse>(
      'GET',
      `/api/orgs/${orgId}/facts${projectId !== undefined ? `?project=${projectId}` : ''}`,
    ),
  searchSourceRows: (orgId: string, demandId: string, lineItemId: string, query: string) =>
    request<SourceRowSearchResponse>(
      'GET',
      `/api/orgs/${orgId}/demands/${demandId}/source-rows?${new URLSearchParams({ line: lineItemId, q: query }).toString()}`,
    ),
  sourceEvidence: (orgId: string, demandId: string) =>
    request<SourceEvidenceResponse>('GET', `/api/orgs/${orgId}/demands/${demandId}/source-evidence`),
  correctSourceMatch: (
    orgId: string,
    kind: 'inventory' | 'purchase',
    recordId: string,
    lineItemId: string,
    status: 'confirmed' | 'rejected',
  ) => request<OkResponse>('PUT', `/api/orgs/${orgId}/source-matches/${kind}/${recordId}/${lineItemId}`, { status }),
  addFact: (orgId: string, body: ManualFact) => request<IdResponse>('POST', `/api/orgs/${orgId}/facts`, body),
  acceptFact: (orgId: string, id: string) => request<OkResponse>('POST', `/api/orgs/${orgId}/facts/${id}/accept`),
  updateFact: (orgId: string, id: string, body: UpdateFact) =>
    request<OkResponse>('PATCH', `/api/orgs/${orgId}/facts/${id}`, body),
  deleteFact: (orgId: string, id: string) => request<OkResponse>('DELETE', `/api/orgs/${orgId}/facts/${id}`),
  archiveDoc: (orgId: string, docId: string, archived: boolean) =>
    request<OkResponse>('POST', `/api/orgs/${orgId}/documents/${docId}/archive`, { archived } satisfies Archive),
  listDocumentRevisions: (orgId: string, docId: string) =>
    request<DocumentRevisionListResponse>('GET', `/api/orgs/${orgId}/documents/${docId}/revisions`),
  async reviseBackground(orgId: string, docId: string, file: File): Promise<ApiResult<IdFileResponse>> {
    const form = new FormData();
    form.set('file', file);
    const res = await fetch(`/api/orgs/${orgId}/documents/${docId}/revisions`, {
      method: 'PUT',
      body: form,
      credentials: 'same-origin',
    });
    const data = (await res.json().catch(() => null)) as unknown;
    const error =
      !res.ok && data !== null && typeof data === 'object' && 'error' in data ? String(data.error) : undefined;
    return { ok: res.ok, status: res.status, data: res.ok ? (data as IdFileResponse) : null, error };
  },
  async uploadBackground(
    orgId: string,
    file: File,
    fields: { category?: string; title?: string; projectId?: string },
  ): Promise<ApiResult<IdResponse>> {
    const form = new FormData();
    form.set('file', file);
    if (fields.category !== undefined) form.set('category', fields.category);
    if (fields.title !== undefined && fields.title !== '') form.set('title', fields.title);
    if (fields.projectId !== undefined) form.set('projectId', fields.projectId);
    const res = await fetch(`/api/orgs/${orgId}/documents`, { method: 'POST', body: form, credentials: 'same-origin' });
    let data: unknown;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    const error =
      !res.ok && data !== null && typeof data === 'object' && 'error' in data ? String(data.error) : undefined;
    return { ok: res.ok, status: res.status, data: res.ok ? (data as IdResponse) : null, error };
  },

  // Analysis report (Flow F4/F5, HS2-CBSYNH / HS2-XDX1HT).
  runAnalysis: (orgId: string, demandId: string) =>
    request<OkJobResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/analyses`),
  listAnalyses: (orgId: string, demandId: string) =>
    request<AnalysisListResponse>('GET', `/api/orgs/${orgId}/demands/${demandId}/analyses`),
  getAnalysis: (orgId: string, demandId: string, analysisId: string) =>
    request<AnalysisReport>('GET', `/api/orgs/${orgId}/demands/${demandId}/analyses/${analysisId}`),
  setDemandAssumptions: (orgId: string, demandId: string, body: DemandAssumptions) =>
    request<OkResponse>('PUT', `/api/orgs/${orgId}/demands/${demandId}/assumptions`, body),
  recomputeAnalysis: (orgId: string, demandId: string, analysisId: string) =>
    request<OkResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/analyses/${analysisId}/recompute`),
  addBenchmark: (orgId: string, demandId: string, analysisId: string, body: ManualBenchmark) =>
    request<OkResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/analyses/${analysisId}/benchmarks`, body),
  confirmHsCode: (orgId: string, demandId: string, analysisId: string, lineId: string) =>
    request<OkResponse>(
      'POST',
      `/api/orgs/${orgId}/demands/${demandId}/analyses/${analysisId}/line-items/${lineId}/confirm-hs-code`,
    ),

  // Decisions (Flow F5, HS2-XDT9YX).
  listDecisions: (orgId: string, demandId: string) =>
    request<DecisionsListResponse>('GET', `/api/orgs/${orgId}/demands/${demandId}/decisions`),
  recordDecision: (orgId: string, demandId: string, lineId: string, body: RecordDecision) =>
    request<OkStatusResponse>('POST', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}/decision`, body),
  clearDecision: (orgId: string, demandId: string, lineId: string) =>
    request<OkStatusResponse>('DELETE', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}/decision`),
  bulkApplyDecisions: (orgId: string, demandId: string, analysisId: string, lineIds: string[] = []) =>
    request<BulkDecisionAck>('POST', `/api/orgs/${orgId}/demands/${demandId}/decisions/bulk-apply`, {
      analysisId,
      ...(lineIds.length > 0 ? { lineIds } : {}),
    } satisfies BulkApplyRecommendationsBody),
  getDecisionSummary: (orgId: string, demandId: string) =>
    request<DecisionSummaryT>('GET', `/api/orgs/${orgId}/demands/${demandId}/decision-summary`),

  // AI chat: demand-scoped (HS2-ZXJJSS) and library/project-scoped (HS2-Q19SYC).
  listConversations: (orgId: string, target: ChatTarget) =>
    request<ConversationListResponse>('GET', `/api/orgs/${orgId}/conversations?${chatQuery(target)}`),
  listProjectConversations: (orgId: string) =>
    request<ConversationListResponse>('GET', `/api/orgs/${orgId}/conversations?scope=project`),
  listOrganizationConversations: (orgId: string) =>
    request<ConversationListResponse>('GET', `/api/orgs/${orgId}/conversations?scope=organization`),
  createConversation: (orgId: string, target: ChatTarget, title?: string) =>
    request<IdResponse>('POST', `/api/orgs/${orgId}/conversations`, {
      ...chatBody(target),
      title,
    } satisfies CreateConversation),
  updateConversationTitle: (orgId: string, cid: string, title: string) =>
    request<OkResponse>('PATCH', `/api/orgs/${orgId}/conversations/${cid}`, {
      title,
    } satisfies UpdateConversation),
  setConversationClosed: (orgId: string, cid: string, closed: boolean) =>
    request<OkResponse>('PATCH', `/api/orgs/${orgId}/conversations/${cid}`, {
      closed,
    } satisfies UpdateConversation),
  getConversation: (orgId: string, cid: string) =>
    request<ConversationDetailResponse>('GET', `/api/orgs/${orgId}/conversations/${cid}`),
  sendChatMessage: (orgId: string, cid: string, text: string) =>
    request<ChatMessageAck>('POST', `/api/orgs/${orgId}/conversations/${cid}/messages`, {
      text,
    } satisfies SendChatMessage),
  applyChatCard: (orgId: string, cid: string, messageId: string, cardIndex: number) =>
    request<ChatApplyAck>('POST', `/api/orgs/${orgId}/conversations/${cid}/messages/${messageId}/apply`, {
      cardIndex,
    } satisfies ChatCardAction),
  dismissChatCard: (orgId: string, cid: string, messageId: string, cardIndex: number) =>
    request<OkResponse>('POST', `/api/orgs/${orgId}/conversations/${cid}/messages/${messageId}/dismiss`, {
      cardIndex,
    } satisfies ChatCardAction),

  // Outcomes & savings (Flow F6, HS2-HBCF6V).
  recordOutcome: (orgId: string, demandId: string, lineId: string, body: RecordOutcome) =>
    request<OutcomeAck>('POST', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}/outcome`, body),
  // Mark a decided line Ordered / edit or undo its order (FR-5.18, HS2-HYDRJX).
  recordOrder: (orgId: string, demandId: string, lineId: string, body: RecordOrder) =>
    request<OkResponse>('PUT', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}/order`, body),
  // Deliveries against the order (FR-5.19, HS2-6AJZPE).
  recordReceipt: (orgId: string, demandId: string, lineId: string, body: RecordReceipt) =>
    request<OutcomeAck>('POST', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}/receipts`, body),
  deleteReceipt: (orgId: string, demandId: string, lineId: string, receiptId: string) =>
    request<OutcomeAck>('DELETE', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}/receipts/${receiptId}`),
  // The vendor failed to deliver: re-source the line (FR-5.21, HS2-SFY5DY).
  failOrder: (orgId: string, demandId: string, lineId: string, body: FailOrder) =>
    request<OutcomeAck>('POST', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}/order/fail`, body),
  reopenShort: (orgId: string, demandId: string, lineId: string) =>
    request<OutcomeAck>('POST', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}/order/reopen-short`),
  clearOrder: (orgId: string, demandId: string, lineId: string) =>
    request<OkResponse>('DELETE', `/api/orgs/${orgId}/demands/${demandId}/line-items/${lineId}/order`),
  getDemandSavings: (orgId: string, demandId: string) =>
    request<DemandSavingsT>('GET', `/api/orgs/${orgId}/demands/${demandId}/savings`),
  getOrgSavings: (orgId: string, query = '') => request<OrgSavingsT>('GET', `/api/orgs/${orgId}/savings${query}`),
};

/** Where a chat conversation lives: a demand, a project, or the org library. */
export type ChatTarget =
  | { scope: 'demand'; demandId: string }
  | { scope: 'library' }
  | { scope: 'project'; projectId: string }
  | { scope: 'organization' };
function chatQuery(t: ChatTarget): string {
  if (t.scope === 'demand') return `scope=demand&demandId=${encodeURIComponent(t.demandId)}`;
  if (t.scope === 'project') return `scope=project&projectId=${encodeURIComponent(t.projectId)}`;
  return t.scope === 'organization' ? 'scope=organization' : 'scope=library';
}
function chatBody(t: ChatTarget): Omit<CreateConversation, 'title'> {
  if (t.scope === 'demand') return { scope: 'demand', demandId: t.demandId };
  if (t.scope === 'project') return { scope: 'project', projectId: t.projectId };
  return t.scope === 'organization' ? { scope: 'organization' } : { scope: 'library' };
}
